import { useMemo, useState } from 'react';
import { ChevronDown, Trash2 } from 'lucide-react';
import type { MultiIdComboboxOption } from '@/components/ui/multi-id-combobox';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  type ResourceSet,
  useResourceSets,
  useUpdateResourceSet,
} from 'components/IAP/hooks/resourceSetHooks';
import type { OrgEventChecklistItemDef } from 'hooks/orgEvents/orgEventAutomationHooks';
import { useOrgEventAutomationRules } from 'hooks/orgEvents/orgEventAutomationHooks';
import { useGetRostersForGroup } from 'hooks/incident/useRoster';
import { cn } from '@/lib/utils';
import { NULL_VALUE } from 'constants/displayConstants';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import { SimpleSelect } from './automationFormControls';
import {
  mergeResourceChecklistForRule,
  resolveResourceAutomationRuleChecklists,
  resourceChecklistUpdatePayload,
  resourceHasAutomationRule,
} from './automationRuleChecklists';
import { filterResourcesAssignedToAutomationRule } from './categoryChecklistMatch';
import { ChecklistConfigEditor } from './ChecklistConfigEditor';
import { DhsResourceDetailsDialog } from '../ResourceTab/DhsResourceDetailsDialog';
import { resourceSetToRow } from '../ResourceTab/resourceSetToRow';
import type { ResourceRow } from '../ResourceTab/ResourceTabCard';

export function CategoryChecklistAssets({
  category,
  ruleName,
  savedRuleName,
  userOptions,
  teamOptions,
}: {
  category: string;
  ruleName?: string;
  /** Persisted rule name used for writes (ignore unsaved renames). */
  savedRuleName?: string;
  userOptions: MultiIdComboboxOption[];
  teamOptions: MultiIdComboboxOption[];
}) {
  const [open, setOpen] = useState(true);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [dialogMode, setDialogMode] = useState<'view' | 'edit'>('view');
  const [rowPatch, setRowPatch] = useState<Partial<ResourceRow> | null>(null);
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: resources = [], isLoading } = useResourceSets();
  const { data: automationRules = [] } = useOrgEventAutomationRules();
  const { data: groupMembers = [] } = useGetRostersForGroup(groupGuid ?? '');
  const writeKey = (savedRuleName ?? ruleName ?? '').trim();
  const displayName = (ruleName ?? category).trim();
  const nameDirty = Boolean(
    writeKey && ruleName?.trim() && writeKey !== ruleName.trim()
  );

  const assigned = useMemo(
    () =>
      writeKey
        ? filterResourcesAssignedToAutomationRule(
            resources,
            writeKey,
            automationRules
          )
        : [],
    [resources, writeKey, automationRules]
  );

  const detailResource = assigned.find(
    (resource) => resource.resource_set_id === detailId
  );
  const detailRow = useMemo(() => {
    if (!detailResource) return null;
    return {
      ...resourceSetToRow(detailResource, groupMembers, automationRules),
      ...rowPatch,
    };
  }, [detailResource, groupMembers, automationRules, rowPatch]);

  const openDetails = (resourceId: string) => {
    setRowPatch(null);
    setDialogMode('view');
    setDetailId(resourceId);
  };

  const addOptions = useMemo(() => {
    if (!writeKey) return [];
    return resources
      .filter(
        (resource) =>
          !resourceHasAutomationRule(resource, writeKey, automationRules)
      )
      .map((resource) => ({
        value: resource.resource_set_id,
        label: resource.name || resource.resource_set_id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources, writeKey, automationRules]);

  if (!displayName && !writeKey) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="tw:space-y-2">
      <CollapsibleTrigger
        render={
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="tw:h-auto tw:justify-start tw:gap-1 tw:whitespace-normal tw:text-left"
          />
        }
      >
        <ChevronDown
          className={cn(
            'tw:h-4 tw:w-4 tw:shrink-0 tw:transition-transform',
            open && 'tw:rotate-180'
          )}
        />
        Per-asset response plans for this rule
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="tw:space-y-2">
          <FieldDescription>
            Playbooks stored on each resource for{' '}
            <span className="tw:font-medium tw:text-foreground">
              {writeKey || displayName}
            </span>
            . These are not the Response Plan. Changes save on the resource
            immediately.
          </FieldDescription>
          {nameDirty ? (
            <p className="tw:m-0 tw:text-xs tw:text-amber-700 tw:dark:text-amber-200">
              Save the rule name before assigning response plans. Writes still
              use “{writeKey}”.
            </p>
          ) : null}
          {!writeKey ? (
            <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
              Save this rule once so assets can be assigned to it.
            </p>
          ) : (
            <>
              <AddAssetToRule
                options={addOptions}
                resources={resources}
                ruleName={writeKey}
              />
              <div className="tw:overflow-x-auto tw:rounded-md tw:border tw:border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Kind</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="tw:w-[130px]">Plan items</TableHead>
                      <TableHead className="tw:w-[160px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="tw:text-xs tw:text-muted-foreground"
                        >
                          Loading assets…
                        </TableCell>
                      </TableRow>
                    ) : assigned.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="tw:text-xs tw:text-muted-foreground"
                        >
                          No assets have a response plan for this automation
                          rule yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      assigned.map((resource) => (
                        <AssignedAssetRow
                          key={resource.resource_set_id || resource.id}
                          resource={resource}
                          ruleName={writeKey}
                          userOptions={userOptions}
                          teamOptions={teamOptions}
                          onOpenDetails={() =>
                            openDetails(resource.resource_set_id)
                          }
                        />
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              <DhsResourceDetailsDialog
                row={detailRow}
                open={detailId != null}
                onOpenChange={(next) => {
                  if (!next) {
                    setDetailId(null);
                    setDialogMode('view');
                    setRowPatch(null);
                  }
                }}
                mode={dialogMode}
                onModeChange={setDialogMode}
                openedInEdit={false}
                groupMembers={groupMembers}
                onRowPatch={(patch) =>
                  setRowPatch((current) => ({ ...current, ...patch }))
                }
              />
            </>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function AddAssetToRule({
  options,
  resources,
  ruleName,
}: {
  options: { value: string; label: string }[];
  resources: ResourceSet[];
  ruleName: string;
}) {
  const [selectedId, setSelectedId] = useState('');
  const resource = resources.find((row) => row.resource_set_id === selectedId);
  const { mutate, isPending } = useUpdateResourceSet(selectedId);
  const { data: automationRules = [] } = useOrgEventAutomationRules();

  const assign = (items: OrgEventChecklistItemDef[]) => {
    if (!resource) return;
    mutate(
      resourceChecklistUpdatePayload(
        resource,
        mergeResourceChecklistForRule({
          resource,
          ruleName,
          items,
          rules: automationRules,
        })
      )
    );
    setSelectedId('');
  };

  return (
    <div className="tw:flex tw:flex-wrap tw:items-end tw:gap-2">
      <Field className="tw:min-w-[220px] tw:flex-1">
        <FieldLabel>Add asset</FieldLabel>
        <SimpleSelect
          value={selectedId || '__none__'}
          onChange={(next) => setSelectedId(next === '__none__' ? '' : next)}
          options={[
            { value: '__none__', label: 'Select a resource…' },
            ...options,
          ]}
          placeholder="Select a resource…"
        />
      </Field>
      <Button
        type="button"
        size="sm"
        disabled={!resource || isPending}
        onClick={() => assign([])}
      >
        Add Asset Response Plan
      </Button>
    </div>
  );
}

function AssignedAssetRow({
  resource,
  ruleName,
  userOptions,
  teamOptions,
  onOpenDetails,
}: {
  resource: ResourceSet;
  ruleName: string;
  userOptions: MultiIdComboboxOption[];
  teamOptions: MultiIdComboboxOption[];
  onOpenDetails: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const { data: automationRules = [] } = useOrgEventAutomationRules();
  const { mutate, isPending } = useUpdateResourceSet(resource.resource_set_id);
  const resolved = resolveResourceAutomationRuleChecklists(
    resource,
    automationRules
  );
  const items = resolved.automationRuleChecklists[ruleName] ?? [];
  const title = resource.name || NULL_VALUE;

  const save = (next: OrgEventChecklistItemDef[], remove = false) => {
    mutate(
      resourceChecklistUpdatePayload(
        resource,
        mergeResourceChecklistForRule({
          resource,
          ruleName,
          items: next,
          remove,
          rules: automationRules,
        })
      )
    );
  };

  return (
    <>
      <TableRow
        className="tw:cursor-pointer"
        onClick={() => setExpanded((open) => !open)}
      >
        <TableCell>
          <button
            type="button"
            className="tw:cursor-pointer tw:border-0 tw:bg-transparent tw:p-0 tw:text-left tw:text-inherit tw:hover:underline"
            onClick={(event) => {
              event.stopPropagation();
              onOpenDetails();
            }}
          >
            {title}
          </button>
        </TableCell>
        <TableCell>{resource.kind || NULL_VALUE}</TableCell>
        <TableCell>{resource.type || NULL_VALUE}</TableCell>
        <TableCell>{items.length}</TableCell>
        <TableCell className="tw:text-right">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Remove ${title}`}
            disabled={isPending}
            onClick={(event) => {
              event.stopPropagation();
              save([], true);
            }}
          >
            <Trash2 className="tw:size-3.5" />
          </Button>
        </TableCell>
      </TableRow>
      {expanded ? (
        <TableRow>
          <TableCell colSpan={5} className="tw:bg-muted/20">
            <div className="tw:space-y-2 tw:py-1">
              <ChecklistConfigEditor
                items={items}
                onChange={(next) => save(next)}
                userOptions={userOptions}
                teamOptions={teamOptions}
                emptyMessage="No per-asset response plan yet."
                addLabel="Add Item"
                reorderable
              />
            </div>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}
